import type { components } from './schema';

/**
 * The error shape the API returns, and how the console turns it into words.
 *
 * The backend never sends user-facing prose — it sends a stable code and the client
 * owns every string. That is what lets one API serve Bulgarian and English without a
 * redeploy, and it means this file is where a failure becomes something a dispatcher
 * can read.
 */

/**
 * Every code the API can return, taken from the generated schema.
 *
 * Not a hand-written list: this is the enum the backend publishes into its OpenAPI
 * document from its own catalogue, so a code that is added, renamed, or removed shows
 * up here the moment the client is regenerated. Branching on a code that no longer
 * exists becomes a compile error rather than a branch that silently never runs.
 */
type ProblemBody = components['schemas']['AppProblemDetails'];

export type ErrorCode = NonNullable<ProblemBody['code']>;

export interface AppProblem {
  /** Stable dotted code, for example `dispatch.driver_unavailable`. */
  code: ErrorCode | (string & {});
  status: number;
  title: string;
  /** English, for logs. Never shown to anyone. */
  detail?: string;
  instance?: string;
  traceId?: string;
  /** Values to substitute into the translation, for example `{ reference: 'A7C3D' }`. */
  arguments?: Record<string, string>;
}

/** Reads a ProblemDetails body, falling back when the response is not one. */
export async function toProblem(response: Response): Promise<AppProblem> {
  try {
    const body = (await response.json()) as Partial<AppProblem>;

    if (typeof body.code === 'string') {
      return {
        code: body.code,
        status: response.status,
        title: body.title ?? body.code,
        detail: body.detail,
        instance: body.instance,
        traceId: body.traceId,
        arguments: body.arguments,
      };
    }
  } catch {
    // A gateway or proxy failing in front of the API returns HTML, not JSON. That is
    // still a real failure and still needs a code the caller can branch on.
  }

  return {
    code: response.status >= 500 ? 'server.unexpected' : 'request.failed',
    status: response.status,
    title: response.statusText,
  };
}

/**
 * Turns a code into a translation key.
 *
 * `dispatch.driver_unavailable` becomes `errors.dispatch.driver_unavailable`, so
 * translations mirror the backend's own vocabulary rather than a parallel one that has
 * to be kept in step by hand.
 */
export function errorKey(problem: AppProblem): string {
  return `errors.${problem.code}`;
}
